import React, { useState, useEffect, useRef } from "react";

import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
	Loader2,
	ChevronDown,
	Check,
	User as UserIcon,
	AlertTriangle,
	Clock,
	Info,
	History,
	Send,
} from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";
import RecorrenciaFields from "../RecorrenciaFields";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";

const ScheduledMessageModal = ({
	open,
	onClose,
	scheduledMessageId,
	onSaved,
	// Preenchida quando o agendamento nasce de um clique num dia do
	// calendário: "yyyy-MM-ddTHH:mm", o mesmo formato do input.
	dataInicial,
	// NOVO: dados do ticket quando o modal é aberto de dentro do atendimento.
	initialTicket,
	initialContact,
	lockedIds = false,
}) => {
	const [contactPopoverOpen, setContactPopoverOpen] = useState(false);
	const [contactOptions, setContactOptions] = useState([]);
	const [searchParam, setSearchParam] = useState("");
	const [searching, setSearching] = useState(false);
	const [selectedContact, setSelectedContact] = useState(null);
	const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
	const [body, setBody] = useState("");
	const [scheduledFor, setScheduledFor] = useState("");
	const [recurrence, setRecurrence] = useState(null);
	const [sendIfMissed, setSendIfMissed] = useState(false);
	const [saving, setSaving] = useState(false);
	// Situação do agendamento carregado — é o que decide se o "Enviar agora"
	// aparece: só faz sentido enquanto ainda está pendente.
	const [status, setStatus] = useState(null);
	const [enviandoAgora, setEnviandoAgora] = useState(false);
	// NO-JS DOC: sugestao do ultimo agendamento, mas NUNCA aplicada automaticamente
	// (o usuario decide clicando no botao).
	const [ultimoModelo, setUltimoModelo] = useState(null);
	const [aplicouUltimo, setAplicouUltimo] = useState(false);
	// Se os campos vieram do agendamento anterior — só para avisar na tela.
	const [veioDoAnterior, setVeioDoAnterior] = useState(false);
	// NOVO: respostas rápidas da empresa (para o select).
	const [quickAnswers, setQuickAnswers] = useState([]);
	const [qaLoading, setQaLoading] = useState(false);
	const [qaSelectedId, setQaSelectedId] = useState("");
	const searchInputRef = useRef(null);

	const { whatsApps, loading: loadingWhatsapps } = useWhatsApps();

	// Um resumo em uma frase: quem recebe, por qual número, quando. Sem isso a
	// pessoa só descobre o que combinou depois que a mensagem já saiu.
	const conexaoEscolhida = whatsApps.find(
		(w) => String(w.id) === String(selectedWhatsappId)
	);
	const quandoLegivel = scheduledFor
		? `${scheduledFor.slice(8, 10)}/${scheduledFor.slice(
				5,
				7
		  )}/${scheduledFor.slice(0, 4)} às ${scheduledFor.slice(11, 16)}`
		: null;
	const jaPassou = scheduledFor
		? new Date(scheduledFor).getTime() < Date.now()
		: false;

	useEffect(() => {
		(async () => {
			if (!scheduledMessageId || !open) return;
			try {
				const { data } = await api.get(
					`/scheduled-messages/${scheduledMessageId}`
				);
				setSelectedContact(data.contact || null);
				setSelectedWhatsappId(data.whatsappId ? String(data.whatsappId) : "");
				setBody(data.body || "");
				setRecurrence(data.recurrence || null);
				setSendIfMissed(Boolean(data.sendIfMissed));
				setStatus(data.status || null);
				if (data.scheduledFor) {
					setScheduledFor(
						format(parseISO(data.scheduledFor), "yyyy-MM-dd'T'HH:mm")
					);
				}
			} catch (err) {
				toastError(err);
			}
		})();
	}, [scheduledMessageId, open]);

	// Agendamento NOVO: busca o ultimo agendamento como SUGESTÃO (nunca aplicada
	// automaticamente). O usuario decide se quer usar, clicando no botão "Usar
	// ultimo agendamento". Isso evita que pessoas mandarem mensagem de domingo
	// repetida sem querer, ao abrir a tela.
	useEffect(() => {
		if (!open || scheduledMessageId) return;

		let ativo = true;

		(async () => {
			try {
				const { data } = await api.get("/scheduled-messages/ultimo-modelo");

				if (!ativo) return;

				// Sempre guarda, mas NUNCA preenche os campos sozinho.
				if (data?.modelo) {
					setUltimoModelo(data.modelo);
				} else {
					setUltimoModelo(null);
				}
			} catch {
				// Sem sugestão a tela segue igual — campos vazios, como antes.
				setUltimoModelo(null);
			}
		})();

		return () => {
			ativo = false;
		};
	}, [open, scheduledMessageId]);

	// Só vale para agendamento novo: numa edição a data já veio do servidor.
	useEffect(() => {
		if (open && !scheduledMessageId && dataInicial) {
			setScheduledFor(dataInicial);
		}
	}, [open, scheduledMessageId, dataInicial]);

	// NOVO: Se o modal for aberto JÁ com um ticket/contato (via cabeçalho do
	// atendimento), preenche os campos de identificação e BLOQUEIA a edição.
	// Guard importante: NÃO aplica em edição (`scheduledMessageId` preenchido) —
	// ali os dados vêm SEMPRE do servidor, nunca do ticket que estava aberto.
	useEffect(() => {
		if (!open || scheduledMessageId) return;
		if (initialContact) {
			setSelectedContact(initialContact);
		}
		if (initialTicket?.whatsappId) {
			setSelectedWhatsappId(String(initialTicket.whatsappId));
		}
	}, [open, scheduledMessageId, initialTicket, initialContact]);

	// NOVO: Carrega as respostas rápidas da empresa (até 5 páginas, ~100 itens)
	// no momento em que o modal abre. Mesmo padrão silencioso do MessageInput:
	// se der erro, não enche a tela de toast — só deixa sem opções no select.
	useEffect(() => {
		if (!open) return;
		let ativo = true;
		(async () => {
			setQaLoading(true);
			try {
				const todas = [];
				let pagina = 1;
				let temMais = true;
				while (temMais && pagina <= 5) {
					// eslint-disable-next-line no-await-in-loop
					const { data } = await api.get("/quickAnswers/", {
						params: { pageNumber: pagina },
					});
					todas.push(...(data?.quickAnswers || []));
					temMais = Boolean(data?.hasMore);
					pagina += 1;
				}
				if (ativo) setQuickAnswers(todas);
			} catch {
				if (ativo) setQuickAnswers([]);
			} finally {
				if (ativo) setQaLoading(false);
			}
		})();
		return () => {
			ativo = false;
		};
	}, [open]);

	useEffect(() => {
		if (!open || searchParam.length < 2) {
			setSearching(false);
			return;
		}
		setSearching(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("/contacts", {
						params: { searchParam },
					});
					setContactOptions(data.contacts || []);
					setSearching(false);
				} catch (err) {
					setSearching(false);
					toastError(err);
				}
			};
			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, open]);

	const handleClose = () => {
		onClose();
		setSelectedContact(null);
		setSelectedWhatsappId("");
		setBody("");
		setScheduledFor("");
		setRecurrence(null);
		setSendIfMissed(false);
		setStatus(null);
		setEnviandoAgora(false);
		setSearchParam("");
		setContactOptions([]);
		// NOVO: limpa estados de resposta rápida também.
		setQuickAnswers([]);
		setQaSelectedId("");
		setVeioDoAnterior(false);
		setUltimoModelo(null);
		setAplicouUltimo(false);
	};

	const handleAplicarUltimoModelo = () => {
		if (!ultimoModelo) return;
		// Aplica mesmo que ja tenha algo escrito (usuario pediu explicitamente
		// para usar o ultimo). Não toca no contato nem na data/hora.
		setBody((atual) => (atual && !aplicouUltimo ? atual : String(ultimoModelo.body || "")));
		setBody(String(ultimoModelo.body || ""));
		setRecurrence(ultimoModelo.recurrence || null);
		setSendIfMissed(Boolean(ultimoModelo.sendIfMissed));
		if (ultimoModelo.whatsappId) {
			setSelectedWhatsappId(String(ultimoModelo.whatsappId));
		}
		setVeioDoAnterior(true);
		setAplicouUltimo(true);
		toast.success("Conteúdo do último agendamento aplicado — edite o que precisar.");
	};

	const handleSelectContact = (contact) => {
		setSelectedContact(contact);
		setContactPopoverOpen(false);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		if (!selectedContact) {
			toast.error("Selecione um contato.");
			return;
		}
		if (!selectedWhatsappId) {
			toast.error("Selecione uma conexão.");
			return;
		}
		if (!body.trim()) {
			toast.error("Digite a mensagem.");
			return;
		}
		if (!scheduledFor) {
			toast.error("Selecione a data e hora.");
			return;
		}

		// Repetição semanal sem nenhum dia marcado é engano de preenchimento, e
		// gravar assim faz o envio cair num dia que ninguém escolheu.
		if (
			recurrence?.tipo === "weekly" &&
			recurrence.diasDaSemana &&
			recurrence.diasDaSemana.length === 0
		) {
			toast.error("Marque ao menos um dia da semana, ou tire a repetição.");
			return;
		}

		if (recurrence?.terminaEm === "") {
			toast.error("Escolha até que data a repetição vai.");
			return;
		}

		const payload = {
			contactId: selectedContact.id,
			whatsappId: +selectedWhatsappId,
			body,
			scheduledFor: new Date(scheduledFor).toISOString(),
			recurrence,
			sendIfMissed,
			// NOVO: víncula o agendamento ao ticket se o modal foi aberto
			// de dentro do atendimento. Evita NPE com initialTicket?.id.
			...(initialTicket?.id && { ticketId: initialTicket.id }),
		};

		setSaving(true);
		try {
			let data;
			if (scheduledMessageId) {
				({ data } = await api.put(
					`/scheduled-messages/${scheduledMessageId}`,
					payload
				));
			} else {
				({ data } = await api.post("/scheduled-messages", payload));
			}
			if (onSaved) {
				onSaved({ ...data, contact: data.contact || selectedContact });
			}
			toast.success("Mensagem agendada com sucesso!");
			handleClose();
		} catch (err) {
			toastError(err);
		}
		setSaving(false);
	};

	// Manda a mensagem na hora, sem esperar a data marcada. Envia a versão JÁ
	// SALVA — texto, hora ou conexão alterados e não salvos no formulário não
	// entram. O servidor usa o mesmo envio do disparo automático e devolve o
	// agendamento já com a data/hora do envio.
	const handleEnviarAgora = async () => {
		if (!scheduledMessageId) return;
		setEnviandoAgora(true);
		try {
			const { data } = await api.post(
				`/scheduled-messages/${scheduledMessageId}/enviar-agora`
			);
			toast.success("Mensagem enviada agora!");
			if (onSaved) {
				onSaved({ ...data, contact: data.contact || selectedContact });
			}
			handleClose();
		} catch (err) {
			toastError(err);
		}
		setEnviandoAgora(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{scheduledMessageId
							? "Editar este envio agendado"
							: "Marcar um envio para um contato"}
					</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSave} className="space-y-4">
					{/* NOVO: aviso quando o agendamento está vinculado a um ticket
					    específico. Destinatário e conexão são fixos — ninguém quer
					    agendar "para este cliente" e acabar mandando para outro por
					    engano de clique. */}
					{lockedIds && initialTicket?.id && (
						<div className="rounded-md bg-blue-50 p-2.5 text-xs leading-relaxed text-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
							<p className="flex gap-1.5">
								<Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
								<span>
									Agendamento vinculado ao ticket{" "}
									<span className="font-semibold">#{initialTicket.id}</span>{" "}
									— destinatário e conexão são fixos desta conversa.
								</span>
							</p>
						</div>
					)}

					{/* NOVO: Select de Resposta Rápida cadastrada. Aplica o texto
					    no body (só se o body ainda estiver vazio, para não
					    sobrescrever o que a pessoa já tinha digitado). */}
					<div className="space-y-1.5">
						<Label>📝 Usar resposta rápida cadastrada</Label>
						<Select
							value={qaSelectedId || undefined}
							onValueChange={(v) => {
								const qa = quickAnswers.find(
									(q) => String(q.id) === String(v)
								);
								if (qa) {
									// Só aplica se o usuário ainda não digitou nada.
									// Igual o MessageInput: resposta rápida é um atalho,
									// não um sobrescreve tudo.
									setBody((atual) => atual || qa.message || "");
								}
								setQaSelectedId(v || "");
							}}
						>
							<SelectTrigger>
								<SelectValue
									placeholder={
										qaLoading
											? "Carregando respostas…"
											: quickAnswers.length > 0
												? "Selecione uma resposta (opcional)"
												: "Nenhuma resposta rápida cadastrada"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{quickAnswers.length === 0 && !qaLoading && (
									<div className="px-2 py-1.5 text-xs text-muted-foreground">
										Cadastre respostas na tela "Respostas Rápidas".
									</div>
								)}
								{quickAnswers.map((qa) => (
									<SelectItem key={qa.id} value={String(qa.id)}>
										<span className="truncate">
											{qa.name}
											{qa.shortcut && (
												<span className="ml-1 text-muted-foreground">
													/{qa.shortcut}
												</span>
											)}
										</span>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							Preenche o texto abaixo automaticamente. Você continua podendo
							editar manualmente depois.
						</p>
					</div>

						<div className="space-y-1.5">
						<Label>Quem vai receber</Label>
						{lockedIds && selectedContact ? (
							// Quando o modal vem de um ticket, NÃO deixa trocar o
							// destinatário — mostra o nome num bloco visualmente
							// igual ao trigger do Popover, mas sem clique.
							<>
								<div
									className={cn(
										"flex h-9 w-full items-center gap-2 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm",
										"cursor-not-allowed opacity-90"
									)}
									title="Destinatário definido pelo ticket atual — não pode ser alterado aqui."
								>
									<UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
									<span className="flex-1 truncate text-left">
										{selectedContact.name}
										{selectedContact.number && (
											<span className="ml-1 text-xs text-muted-foreground">
												({selectedContact.number})
											</span>
										)}
									</span>
									<Clock className="h-4 w-4 shrink-0 opacity-50" />
								</div>
								<p className="text-xs text-muted-foreground">
									Mensagem será enviada para o contato desta conversa.
								</p>
							</>
						) : (
							// Caminho normal: modal aberto da página geral de
							// agendamentos, ou edição — pode escolher qualquer contato.
							<Popover
								open={contactPopoverOpen}
								onOpenChange={(o) => {
									setContactPopoverOpen(o);
									if (o) setTimeout(() => searchInputRef.current?.focus(), 0);
								}}
							>
								<PopoverTrigger asChild>
									<button
										type="button"
										className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs focus:outline-hidden focus:ring-1 focus:ring-ring"
									>
										<UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
										<span
											className={cn(
												"flex-1 truncate text-left",
												!selectedContact && "text-muted-foreground"
											)}
										>
											{selectedContact
												? selectedContact.name
												: "Buscar contato"}
										</span>
										<ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
									</button>
								</PopoverTrigger>
								<PopoverContent
									className="w-(--radix-popover-trigger-width) p-1"
									align="start"
								>
									<Input
										ref={searchInputRef}
										placeholder="Digite para buscar"
										value={searchParam}
										onChange={(e) => setSearchParam(e.target.value)}
										className="mb-1"
									/>
									<div className="max-h-52 overflow-y-auto">
										{searching && (
											<div className="flex items-center justify-center py-3">
												<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
											</div>
										)}
										{!searching &&
											searchParam.length >= 2 &&
											contactOptions.length === 0 && (
												<p className="px-2 py-1.5 text-sm text-muted-foreground">
													Nenhum contato encontrado
												</p>
											)}
										{!searching &&
											contactOptions.map((contact) => (
												<button
													type="button"
													key={contact.id}
													onClick={() => handleSelectContact(contact)}
													className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
												>
													<Check
														className={cn(
															"h-3.5 w-3.5 shrink-0",
															selectedContact?.id === contact.id
																? "opacity-100"
																: "opacity-0"
														)}
													/>
													<span className="flex-1 truncate">{contact.name}</span>
													<span className="text-xs text-muted-foreground">
														{contact.number}
													</span>
												</button>
											))}
									</div>
								</PopoverContent>
							</Popover>
						)}
					</div>

					<div className="space-y-1.5">
						<Label>De qual número sai</Label>
						<Select
							value={selectedWhatsappId || undefined}
							onValueChange={(v) => setSelectedWhatsappId(v)}
							disabled={lockedIds}
						>
							<SelectTrigger>
								<SelectValue
									placeholder={
										loadingWhatsapps ? "Carregando..." : "Selecione a conexão"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{whatsApps.map((whatsapp) => (
									<SelectItem key={whatsapp.id} value={String(whatsapp.id)}>
										{whatsapp.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							{lockedIds
								? "Pela conexão usada nesta conversa."
								: "É esse o número que vai aparecer no celular do contato."}
						</p>
					</div>

					<div className="space-y-1.5">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<Label htmlFor="body">O que será enviado</Label>
							{!scheduledMessageId && ultimoModelo && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={handleAplicarUltimoModelo}
									disabled={aplicouUltimo}
									className="gap-1.5"
								>
									<History className="h-3.5 w-3.5" />
									{aplicouUltimo
										? "Texto do último aplicado"
										: "Usar texto do último agendamento"}
								</Button>
							)}
						</div>
						<Textarea
							id="body"
							rows={4}
							value={body}
							onChange={(e) => setBody(e.target.value)}
						/>
						{veioDoAnterior && (
							<p className="text-xs leading-relaxed text-muted-foreground">
								Preenchido com o último agendamento feito aqui — texto,
								recorrência e conexão. Altere o que precisar.
							</p>
						)}
						{!scheduledMessageId && ultimoModelo && !veioDoAnterior && !body.trim() && (
							<p className="text-xs leading-relaxed text-muted-foreground">
								Tem um agendamento anterior salvo. Clique em
								<strong> Usar texto do último agendamento </strong> acima para
								começar com ele, ou digite algo novo aqui.
							</p>
						)}
						<p className="text-xs text-muted-foreground">
							Escreva como você falaria — o contato recebe exatamente este texto,
							sem nenhum aviso de que é automático.
						</p>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="scheduledFor">Quando enviar</Label>
						<Input
							id="scheduledFor"
							type="datetime-local"
							value={scheduledFor}
							onChange={(e) => setScheduledFor(e.target.value)}
						/>
						<p className="text-xs text-muted-foreground">
							O sistema confere a cada minuto. Hora que já passou há mais de 15
							minutos só é enviada se você ligar a recuperação abaixo.
						</p>
					</div>

					<RecorrenciaFields
						recurrence={recurrence}
						onRecurrenceChange={setRecurrence}
						sendIfMissed={sendIfMissed}
						onSendIfMissedChange={setSendIfMissed}
					/>

					{/* Confirmação em uma frase, antes de salvar. */}
					<div className="rounded-md bg-muted p-2.5 text-xs leading-relaxed">
						<p className="flex gap-1.5">
							<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
							<span>
								{selectedContact && quandoLegivel ? (
									<>
										Vamos enviar essa mensagem para{" "}
										<span className="font-medium">{selectedContact.name}</span>
										{selectedContact.number ? ` (${selectedContact.number})` : ""}{" "}
										em <span className="font-medium">{quandoLegivel}</span>
										{conexaoEscolhida ? `, pela conexão ${conexaoEscolhida.name}` : ""}
										. Ninguém precisa estar com o sistema aberto.
										{jaPassou &&
											(sendIfMissed
												? " Como essa hora já passou, o envio sai em até um minuto."
												: " Essa hora já passou: ligue a recuperação abaixo, senão o envio será pulado.")}
									</>
								) : (
									"Escolha o contato e a data para ver aqui, em uma frase, o que vai acontecer."
								)}
							</span>
						</p>
						<p className="mt-1.5 text-muted-foreground">
							Depois de salvar, dá para impedir o envio clicando em “Cancelar o
							envio” na lista — mas só antes da hora marcada.
						</p>
					</div>

					<DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
						<div className="flex">
							{scheduledMessageId && status === "pending" && (
								<Button
									type="button"
									variant="outline"
									onClick={handleEnviarAgora}
									disabled={saving || enviandoAgora}
									title="Envia agora a mensagem já salva, sem esperar a hora marcada. O que estiver alterado e não salvo no formulário não é enviado."
								>
									{enviandoAgora ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<Send className="h-4 w-4" />
									)}
									ENVIAR AGORA
								</Button>
							)}
						</div>
						<div className="flex gap-2">
							<Button
								type="button"
								variant="outline"
								onClick={handleClose}
								disabled={saving || enviandoAgora}
							>
								Cancelar
							</Button>
							<Button type="submit" disabled={saving || enviandoAgora}>
								{saving && <Loader2 className="h-4 w-4 animate-spin" />}
								{scheduledMessageId ? "Salvar alterações" : "Marcar envio"}
							</Button>
						</div>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default ScheduledMessageModal;
